import z from "zod";

const agentActionsErrorTypeValues = [
  "ACTION_NOT_FOUND",
  "ACTION_UNAUTHORIZED",
  "ACTION_EXECUTION_FAILED",
  "ACTION_INPUT_INVALID",
  "NO_OPENPHONE_LINE_FOR_REP",
  // Collapsed shape: same error type whether the phone doesn't exist, is on a
  // different bid, or belongs to a different org. Prevents the agent from using
  // distinct responses to probe across bid/org boundaries.
  "PHONE_NOT_ON_BID",
  "CONVERSATION_NOT_LINKED_TO_BID",
  "CONVERSATION_NOT_SMS_CAPABLE",
  "CONVERSATION_NOT_ADDRESSABLE",
  "CONVERSATION_NOT_ON_CS_LINE",
  "CONVERSATION_CHANNEL_NOT_AGENT_POSTABLE",
  "CONVERSATION_NOT_IN_PARTNER_UPDATE_COHORT",
  // Collapsed shape: same error whether the phone call doesn't exist or belongs
  // to a different org. Prevents the agent from probing cross-org existence.
  "PHONE_CALL_NOT_FOUND",
  "MENTION_TEXT_ID_MISMATCH",
  "MENTION_NOT_ALLOWED",
  "SQL_QUERY_INVALID",
  "QUICKBOOKS_QUERY_INVALID",
] as const;

const agentActionsErrorTypeSchema = z.enum(agentActionsErrorTypeValues);
const AgentActionsErrorType = agentActionsErrorTypeSchema.enum;
type AgentActionsErrorType = z.infer<typeof agentActionsErrorTypeSchema>;

type ErrorDetails = {
  sentryDescription: string;
};

export type AgentActionsErrors =
  | {
      errorType: typeof AgentActionsErrorType.ACTION_NOT_FOUND;
      data: {
        actionName: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.ACTION_UNAUTHORIZED;
      data: {
        actionName: string;
        userId: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.ACTION_EXECUTION_FAILED;
      data: {
        actionName: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.ACTION_INPUT_INVALID;
      data: {
        actionName: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.NO_OPENPHONE_LINE_FOR_REP;
      data: {
        personId: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.PHONE_NOT_ON_BID;
      data: {
        bidId: number;
        phoneId: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.CONVERSATION_NOT_LINKED_TO_BID;
      data: {
        conversationId: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.CONVERSATION_NOT_SMS_CAPABLE;
      data: {
        conversationId: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.CONVERSATION_NOT_ADDRESSABLE;
      data: {
        conversationId: string;
        homeownerCount: number;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.CONVERSATION_NOT_ON_CS_LINE;
      data: {
        conversationId: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.CONVERSATION_CHANNEL_NOT_AGENT_POSTABLE;
      data: {
        conversationId: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.CONVERSATION_NOT_IN_PARTNER_UPDATE_COHORT;
      data: {
        conversationId: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.PHONE_CALL_NOT_FOUND;
      data: {
        phoneCallId: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.MENTION_TEXT_ID_MISMATCH;
      data: {
        missingFromText: string[];
        missingFromParams: string[];
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.MENTION_NOT_ALLOWED;
      data: {
        disallowed: string[];
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.SQL_QUERY_INVALID;
      data: {
        query: string;
      };
    }
  | {
      errorType: typeof AgentActionsErrorType.QUICKBOOKS_QUERY_INVALID;
      data: {
        entity: string;
        query: string;
        faultCode?: string;
      };
    };

export const agentActionsErrorDetails: Record<AgentActionsErrorType, ErrorDetails> = {
  [AgentActionsErrorType.ACTION_NOT_FOUND]: {
    sentryDescription: "Requested record not found or not visible to the caller",
  },
  [AgentActionsErrorType.ACTION_UNAUTHORIZED]: {
    sentryDescription: "User lacks permission for action",
  },
  [AgentActionsErrorType.ACTION_EXECUTION_FAILED]: {
    sentryDescription: "Action execution failed",
  },
  [AgentActionsErrorType.ACTION_INPUT_INVALID]: {
    sentryDescription: "Action input is valid by schema but not allowed for this record or caller",
  },
  [AgentActionsErrorType.NO_OPENPHONE_LINE_FOR_REP]: {
    sentryDescription: "Calling rep has no OpenPhone line provisioned",
  },
  [AgentActionsErrorType.PHONE_NOT_ON_BID]: {
    sentryDescription: "Phone does not belong to a contact on the specified bid",
  },
  [AgentActionsErrorType.CONVERSATION_NOT_LINKED_TO_BID]: {
    sentryDescription: "Conversation is not linked to any bid",
  },
  [AgentActionsErrorType.CONVERSATION_NOT_SMS_CAPABLE]: {
    sentryDescription: "Conversation is not a homeowner SMS thread",
  },
  [AgentActionsErrorType.CONVERSATION_NOT_ADDRESSABLE]: {
    sentryDescription: "Conversation does not resolve to exactly one homeowner recipient",
  },
  [AgentActionsErrorType.CONVERSATION_NOT_ON_CS_LINE]: {
    sentryDescription: "Conversation's Remi-side number is not the CS line",
  },
  [AgentActionsErrorType.CONVERSATION_CHANNEL_NOT_AGENT_POSTABLE]: {
    sentryDescription: "Conversation's channel is not one the MCP agent may post to",
  },
  [AgentActionsErrorType.CONVERSATION_NOT_IN_PARTNER_UPDATE_COHORT]: {
    sentryDescription: "Conversation is not currently flagged for a partner check-in",
  },
  [AgentActionsErrorType.PHONE_CALL_NOT_FOUND]: {
    sentryDescription: "Phone call not found or belongs to a different org",
  },
  [AgentActionsErrorType.MENTION_TEXT_ID_MISMATCH]: {
    sentryDescription: "Mention markers in the text do not match the supplied mention id arrays",
  },
  [AgentActionsErrorType.MENTION_NOT_ALLOWED]: {
    sentryDescription: "A supplied mention id is not mentionable on this conversation",
  },
  [AgentActionsErrorType.SQL_QUERY_INVALID]: {
    sentryDescription: "Agent-submitted SQL query failed with a database syntax or column error",
  },
  [AgentActionsErrorType.QUICKBOOKS_QUERY_INVALID]: {
    sentryDescription: "QuickBooks rejected the agent-submitted query",
  },
};
